//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl, MakeUrlTokens } from 'utils/UrlUtil';

import { injectEndpoints, tags } from '../api';
import { CustomActionPythonPayload } from './types';
import { transformToCustomActionPython } from './util';

const customActionPythonApi = injectEndpoints({
  endpoints: (builder) => ({
    // Get a Python custom action
    getCustomActionPython: builder.query<CustomActionPythonPayload, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_ITEM, { functionId: params.functionId } as MakeUrlTokens),
          method: 'GET',
        };
      },
      transformResponse: transformToCustomActionPython,
      providesTags: (result) => (result?.id ? [tags.CustomActionPython(result.id)] : []),
    }),
    // Get Python custom action draft
    getCustomActionPythonDraft: builder.query<CustomActionPythonPayload, { functionId: string; action?: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_GET_DRAFT, {
            functionId: params.functionId,
          } as MakeUrlTokens),
          method: 'GET',
        };
      },
      transformResponse: transformToCustomActionPython,
    }),
    // Create a draft of a Python custom action
    createCustomActionPython: builder.mutation<
      CustomActionPythonPayload,
      CustomActionPythonPayload & Record<string, string>
    >({
      query: (params) => {
        const { id, ...body } = params;
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_CREATE_DRAFT),
          method: 'POST',
          body,
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: [tags.CustomActionList],
    }),
    // Update a Python custom action
    updateCustomActionPython: builder.mutation<
      CustomActionPythonPayload,
      CustomActionPythonPayload & Record<string, string>
    >({
      query: (params) => {
        const { id, ...body } = params;
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_UPDATE_DRAFT, { functionId: id } as MakeUrlTokens),
          method: 'PUT',
          body,
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: (result) => [tags.CustomActionList, ...(result?.id ? [tags.CustomActionPython(result.id)] : [])],
    }),
    // Publish a Python custom action
    publishCustomActionPython: builder.mutation<void, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_PUBLISH, {
            functionId: params.functionId,
          } as MakeUrlTokens),
          method: 'POST',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
    // Create a draft from a published Python custom action
    createCustomActionPythonDraft: builder.mutation<CustomActionPythonPayload, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_NEW_DRAFT, params as MakeUrlTokens),
          method: 'POST',
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: [tags.CustomActionList],
    }),
    // Delete a Python custom action
    deleteCustomActionPython: builder.mutation<void, Record<string, string>>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_DELETE, params),
          method: 'DELETE',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
    // Share a Python custom action
    shareCustomActionPython: builder.mutation<void, Record<string, any>>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_SHARE, params),
          method: 'POST',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
  }),
});

export const {
  useGetCustomActionPythonQuery,
  useLazyGetCustomActionPythonQuery,
  useGetCustomActionPythonDraftQuery,
  useLazyGetCustomActionPythonDraftQuery,
  useCreateCustomActionPythonMutation,
  useCreateCustomActionPythonDraftMutation,
  useDeleteCustomActionPythonMutation,
  useUpdateCustomActionPythonMutation,
  usePublishCustomActionPythonMutation,
  useShareCustomActionPythonMutation,
} = customActionPythonApi;
